import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";

import { clearCredentials, setCredentials } from "./slices/authSlice";
import {
  getGroupedVariantParentsHeader,
  GROUPED_VARIANT_PARENTS_HEADER,
} from "@/src/features/cart/groupedVariantParents";
import type { RootState } from "./index";

// Route client-side data requests through Next.js API handlers so auth refresh,
// backend cookies, and guest/session flows stay consistent across features.
const apiBaseUrl = "/api/";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: apiBaseUrl,
  credentials: "same-origin",
  prepareHeaders: (headers, { getState }) => {
    const accessToken = (getState() as RootState).auth.accessToken;

    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    const groupedVariantParents = getGroupedVariantParentsHeader();

    if (groupedVariantParents) {
      headers.set(GROUPED_VARIANT_PARENTS_HEADER, groupedVariantParents);
    }

    return headers;
  },
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(api: {
  dispatch: (action: unknown) => unknown;
  getState: () => unknown;
}) {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const response = await fetch("/api/auth", {
        method: "GET",
        cache: "no-store",
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error("TOKEN_REFRESH_FAILED");
      }

      const data = (await response.json()) as { access?: string };

      if (!data.access) {
        throw new Error("TOKEN_REFRESH_MISSING_ACCESS");
      }

      const state = api.getState() as RootState;

      api.dispatch(
        setCredentials({
          accessToken: data.access,
          userId: state.auth.userId,
        }),
      );

      return data.access;
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);
  const isUnauthorized = result.error?.status === 401;
  const requestUrl = typeof args === "string" ? args : args.url;
  const isAuthRequest = requestUrl.includes("/auth");
  const authState = (api.getState() as RootState).auth;
  const hasAuthenticatedSession =
    authState.isAuthenticated && Boolean(authState.accessToken);

  // Guests may legitimately receive 401 from user-only endpoints. Refreshing
  // their session then resetting the API cache causes subscribed queries to
  // start again indefinitely.
  if (!isUnauthorized || isAuthRequest || !hasAuthenticatedSession) {
    return result;
  }

  try {
    const nextAccessToken = await refreshAccessToken(api);

    if (!nextAccessToken) {
      throw new Error("TOKEN_REFRESH_EMPTY");
    }

    result = await rawBaseQuery(args, api, extraOptions);
  } catch {
    api.dispatch(clearCredentials());

    void fetch("/api/auth", {
      method: "DELETE",
      cache: "no-store",
      credentials: "same-origin",
    });
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [],
  endpoints: () => ({}),
});
